'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';
export type ToastMessage = ReactNode;

export interface ToastApi {
  show: (message: ToastMessage, severity?: ToastSeverity) => void;
  success: (message: ToastMessage) => void;
  error: (message: ToastMessage) => void;
  warning: (message: ToastMessage) => void;
  info: (message: ToastMessage) => void;
}

interface ToastState {
  open: boolean;
  message: ToastMessage;
  severity: ToastSeverity;
}

const INITIAL_STATE: ToastState = { open: false, message: '', severity: 'info' };
const AUTO_HIDE_MS = 5000;

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ToastState>(INITIAL_STATE);

  const show = useCallback((message: ToastMessage, severity: ToastSeverity = 'info') => {
    setState({ open: true, message, severity });
  }, []);

  const handleClose = useCallback((_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message) => show(message, 'success'),
      error: (message) => show(message, 'error'),
      warning: (message) => show(message, 'warning'),
      info: (message) => show(message, 'info'),
    }),
    [show]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={AUTO_HIDE_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => handleClose()}
          severity={state.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
